import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { CHANGE_USERNAME } from '../../graphql/member';
import { GET_PROFILE } from '../../graphql/profile/queries';

type Args = {
    memberId?: string;
    initUsername?: string;
};

export const useUsernamePreferences = ({ initUsername }: Args) => {
    const [username, setUsername] = useState<string | undefined>(initUsername);
    const [input, setInput] = useState<string>(initUsername ?? '');
    const [message, setMessage] = useState<string | undefined>(undefined);
    const [localBusy, setLocalBusy] = useState(false);
    const [dirty, setDirty] = useState(false);

    const { data, loading } = useQuery(GET_PROFILE, {
        fetchPolicy: 'no-cache',
    });

    useEffect(() => {
        console.log('MemberId', data?.profile?.id);
    }, [data, loading]);

    const [changeUsernameMutation, { loading: editing }] = useMutation(CHANGE_USERNAME);

    const effectiveMemberId = data?.profile?.id;

    const setInputValue = useMemo(
        () => (value: string) => {
            setDirty(value === input ? false : true);
            setInput(value);
            setMessage(undefined);
        },
        []
    );

    const save = useMemo(
        () => async (): Promise<{ ok: boolean; message?: string; newUsername?: string }> => {
            const proposed = (input ?? '').trim();

            if (!proposed) {
                const msg = 'Please enter a username.';
                setMessage(msg);
                return { ok: false, message: msg };
            }

            if (!effectiveMemberId) {
                const msg = 'Could not resolve your account (no memberId from username lookup).';
                setMessage(msg);
                return { ok: false, message: msg };
            }

            if (proposed === username) {
                return { ok: true, newUsername: username };
            }

            try {
                setLocalBusy(true);
                await changeUsernameMutation({
                    variables: { memberId: effectiveMemberId, username: proposed },
                });

                setUsername(proposed);
                setDirty(false);
                setMessage(undefined);

                return { ok: true, newUsername: proposed };
            } catch (e: any) {
                const msg =
                    e?.graphQLErrors?.[0]?.message || e?.message || 'Could not change username.';
                setMessage(msg);
                return { ok: false, message: msg };
            } finally {
                setLocalBusy(false);
            }
        },
        [initUsername, effectiveMemberId, input, username, changeUsernameMutation]
    );

    const inProgress = editing || localBusy;

    return {
        models: {
            username,
            input,
            inProgress,
            message,
            dirty,
            hasMemberId: !!effectiveMemberId,
        },
        operations: {
            setInput: setInputValue,
            save,
        },
    };
};
